import { Peer, type PeerDeps } from "./peer";
import { cyrb53 } from "../lib/hash";
import { waitForIceGatheringComplete } from "../lib/room-helpers";
import type { SignalType } from "./bus";

function fingerprintOf(sdp: string): string {
  for (const line of sdp.split("\r\n")) {
    if (line.startsWith("a=fingerprint:")) return line.substring(14);
  }
  return "";
}

/** Pure: derive the shared 16+ digit connection hash from both SDP fingerprints.
 *  Caller and callee must agree, so the combination is role-ordered. */
export function connectionHash(localSdp: string, remoteSdp: string, isCaller: boolean): string {
  const local = fingerprintOf(localSdp);
  const remote = fingerprintOf(remoteSdp);
  const combined = isCaller ? local + remote : remote + local;
  let hash = cyrb53(combined).toString();
  while (hash.length < 16) hash = "0" + hash;
  return hash;
}

export type RTCPeerDeps = PeerDeps & { rtcConfig: RTCConfiguration };

export class RTCPeer extends Peer {
  private readonly rtcConfig: RTCConfiguration;
  private _conn: RTCPeerConnection | null = null;
  private _channel: RTCDataChannel | null = null;

  constructor(deps: RTCPeerDeps) {
    super(deps);
    this.rtcConfig = deps.rtcConfig;
    if (this._isCaller) this._connect();
  }

  private _connect(): void {
    if (!this._conn || this._conn.signalingState === "closed") this._openConnection();
    if (this._isCaller) this._openChannel();
    else this._conn!.ondatachannel = (e) => this._onChannelOpened(e.channel);
  }

  private _openConnection(): void {
    const conn = new RTCPeerConnection(this.rtcConfig);
    conn.onconnectionstatechange = () => this._onConnectionStateChange();
    conn.oniceconnectionstatechange = () => this._onIceConnectionStateChange();
    this._conn = conn;
  }

  private _openChannel(): void {
    if (!this._conn) return;
    const channel = this._conn.createDataChannel("data-channel", { ordered: true });
    channel.onopen = () => this._onChannelOpened(channel);
    channel.onerror = (e) => this._onError(e);
    this._conn.createOffer()
      .then((d) => this._onDescription(d))
      .catch((e) => this._onError(e));
  }

  private async _onDescription(description: RTCSessionDescriptionInit): Promise<void> {
    await this._conn!.setLocalDescription(description);
    this.bus.emit("handshake-active", true);
    try {
      await waitForIceGatheringComplete(this._conn!, 2000);
      this._sendSignal((description.type as SignalType), this._conn!.localDescription!);
    } finally {
      this.bus.emit("handshake-active", false);
    }
  }

  /** Called by PeersManager when a signal for this peer arrives. */
  onServerMessage(message: { type: SignalType; payload: { sdp: RTCSessionDescriptionInit } }): void {
    if (!this._conn) this._connect();
    const sdp = message.payload?.sdp;
    if (!sdp) return;
    this._conn!.setRemoteDescription(sdp)
      .then(() => {
        if (sdp.type === "offer") {
          return this._conn!.createAnswer().then((d) => this._onDescription(d));
        }
      })
      .catch((e) => this._onError(e));
  }

  private _onChannelOpened(channel: RTCDataChannel): void {
    channel.binaryType = "arraybuffer";
    channel.onmessage = (e) => this.onMessage(e.data);
    channel.onclose = () => this._onChannelClosed();
    this._channel = channel;
    this.bus.emit("peer-connected", { peerId: this._peerId, connectionHash: this.getConnectionHash() });
  }

  getConnectionHash(): string {
    return connectionHash(this._conn!.localDescription!.sdp, this._conn!.remoteDescription!.sdp, this._isCaller);
  }

  disconnect(): void {
    if (this._conn && this._channel) {
      this._channel.onclose = null;
      this._channel.close();
    }
    this.bus.emit("peer-disconnected", this._peerId);
  }

  private _onChannelClosed(): void {
    this.bus.emit("peer-disconnected", this._peerId);
    if (this._isCaller) this._connect();
  }

  private _onConnectionStateChange(): void {
    const state = this._conn?.connectionState;
    if (state === "disconnected" || state === "failed") {
      this.bus.emit("peer-disconnected", this._peerId);
      this._onError(`rtc connection ${state}`);
    }
  }

  private _onIceConnectionStateChange(): void {
    if (this._conn?.iceConnectionState === "failed") this._onError("ICE Gathering failed");
  }

  private _onError(error: unknown): void {
    console.error(error);
  }

  protected _send(message: string | ArrayBuffer): void {
    if (!this._channel) { this.refresh(); return; }
    this._channel.send(message as string);
  }

  private _sendSignal(type: SignalType, sdp: RTCSessionDescriptionInit): void {
    this.bus.emit("signal-send", { to: this._peerId, type, payload: { sdp } });
  }

  refresh(): void {
    if (this._isConnected() || this._isConnecting()) return;
    if (!this._isCaller) return;
    this._connect();
  }

  private _isConnected(): boolean { return this._channel?.readyState === "open"; }
  private _isConnecting(): boolean { return this._channel?.readyState === "connecting"; }

  get conn(): RTCPeerConnection | null { return this._conn; }
  get channel(): RTCDataChannel | null { return this._channel; }
  set channel(c: RTCDataChannel | null) { this._channel = c; }

  override sendDisplayName(displayName: string): void {
    if (!this._isConnected()) return;
    super.sendDisplayName(displayName);
  }
}
